import { Lock, ShieldCheck, Globe, BadgeCheck } from "lucide-react";

/**
 * SSLVerifiedBadge
 * ─────────────────────────────────────────────────────────────────────
 * Customer-facing trust certification card showing that travelsate.in is
 * served over HTTPS with a valid SSL certificate. Paired beside the
 * RazorpayTrustBadge on LandingPage + AboutPage and used as a compact
 * strip on BookingPage Step 6.
 *
 * The four trust signals reflect the actual SSL configuration verified
 * against the production domain (curl -I https://travelsate.in):
 *   • Google Trust Services CA (the issuer)
 *   • TLS 1.3 (modern transport encryption)
 *   • 256-bit AES (cipher strength)
 *   • HSTS Preload (Strict-Transport-Security max-age=63072000 with
 *     `preload` directive — see Iteration 36)
 *
 * `variant="full"`   → large card matching RazorpayTrustBadge's full size.
 * `variant="compact"`→ horizontal strip for the Step-6 payment summary.
 */
export function SSLVerifiedBadge({ variant = "full", className = "" }) {
  const pills = [
    { icon: BadgeCheck, label: "Google Trust Services CA" },
    { icon: ShieldCheck, label: "TLS 1.3" },
    { icon: Lock,        label: "256-bit AES" },
    { icon: ShieldCheck, label: "HSTS Preload" },
  ];

  if (variant === "compact") {
    return (
      <div
        data-testid="ssl-verified-badge-compact"
        className={`flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-emerald-400/15 bg-emerald-400/[0.03] px-4 py-3 ${className}`}
      >
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-emerald-500/15 border border-emerald-400/30">
            <Lock className="w-3.5 h-3.5 text-emerald-300" />
          </div>
          <div className="leading-tight">
            <p className="text-white/85 text-xs font-medium tracking-wide">SSL Verified</p>
            <p className="text-emerald-300/70 text-[10px] tracking-wider">travelsate.in</p>
          </div>
        </div>
        <span className="hidden sm:inline h-3.5 w-px bg-white/15" />
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          {pills.map(({ icon: Icon, label }) => (
            <span key={label} className="inline-flex items-center gap-1 text-[10px] text-white/55 tracking-wide">
              <Icon className="w-3 h-3 text-emerald-300/80" />
              {label}
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="ssl-verified-badge-full"
      className={`relative overflow-hidden rounded-xl border border-emerald-400/15 bg-gradient-to-br from-emerald-500/[0.06] via-transparent to-emerald-500/[0.02] p-6 sm:p-7 ${className}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-7">
        {/* Lock-in-shield visual standing in for an SSL "lock" badge —
            matches the bank-statement aesthetic of the Razorpay tile next
            to it so the two read as a paired trust certification set. */}
        <div className="shrink-0 flex items-center justify-center">
          <div className="relative w-[68px] h-[68px] rounded-xl bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center shadow-md shadow-black/40">
            <Lock className="w-7 h-7 text-emerald-300" />
            <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500 border-2 border-black/80">
              <BadgeCheck className="w-3 h-3 text-black" />
            </span>
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <Globe className="w-4 h-4 text-emerald-300" />
            <h3 className="text-white font-medium text-sm tracking-wide">
              Verified by SSL — <span className="text-emerald-300">travelsate.in</span>
            </h3>
          </div>
          <p className="text-white/55 text-xs leading-relaxed mb-3.5 max-w-xl">
            Every page you view on Travelsate is delivered over a fully encrypted HTTPS connection. Your booking details, payment redirects, and account credentials are protected end-to-end by a Google Trust Services certificate and TLS 1.3 transport security.
          </p>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
            {pills.map(({ icon: Icon, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.03] border border-emerald-400/15 text-[10.5px] text-white/70 tracking-wide"
              >
                <Icon className="w-3 h-3 text-emerald-300" />
                {label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SSLVerifiedBadge;
